
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { BasicInfoFields } from "./form/BasicInfoFields";
import { CompanyFields } from "./form/CompanyFields";
import { LocationFields } from "./form/LocationFields";
import { AdditionalFields } from "./form/AdditionalFields";
import { CategoryField } from "./form/CategoryField";
import { UserSelector } from "@/components/common/UserSelector";
import { contactFormSchema, type ContactFormValues } from "./form/types";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useState } from "react";

interface ContactFormProps {
  onSuccess?: () => void;
  initialData?: ContactFormValues;
  onCancel?: () => void;
}

export const ContactForm = ({ onSuccess, initialData, onCancel }: ContactFormProps) => {
  const { toast } = useToast();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: initialData || {
      name: "",
      email: "",
      phone: "",
      company: "",
      country: "",
      city: "",
      role: "",
      website: "",
      lead_source: "",
      category: "prospect",
      assigned_to: "",
    },
  });

  const handleSubmit = async (values: ContactFormValues, createAnother: boolean = false) => {
    if (!session?.user?.id) {
      toast({
        title: "Error",
        description: "You must be logged in to perform this action",
        variant: "destructive",
      });
      return;
    }

    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }

    if (!values.name.trim()) {
      toast({
        title: "Error",
        description: "Name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      if (initialData?.id) {
        // Update existing contact using consolidated function
        const { data, error } = await supabase.functions.invoke("contact-operations", {
          body: {
            action: "update",
            contact: {
              ...values,
              name: values.name.trim(),
              organization_id: initialData.organization_id || currentOrganization.id
            },
            contactId: initialData.id
          }
        });
          
        if (error) {
          console.error("Error updating contact:", error);
          throw error;
        }
      } else {
        // Create new contact using consolidated function
        const { data, error } = await supabase.functions.invoke("contact-operations", {
          body: {
            action: "create",
            contact: {
              ...values,
              name: values.name.trim(),
            },
            userId: session.user.id,
            organizationId: currentOrganization.id
          }
        });
        
        if (error) {
          console.error("Error creating contact:", error);
          throw error;
        }
      }

      // Invalidate queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      
      form.reset();
      
      if (!createAnother) {
        onSuccess?.();
      }
      
      toast({
        title: "Success",
        description: `Contact ${initialData ? 'updated' : 'added'} successfully`,
      });
    } catch (error: any) {
      console.error('Error in contact form submission:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save contact",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // If no session, show a message
  if (!session) {
    return <div>Please log in to manage contacts.</div>;
  }

  // If no organization, show a message
  if (!currentOrganization) {
    return <div>Please select an organization to manage contacts.</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => handleSubmit(values, false))} className="space-y-4">
        <BasicInfoFields form={form} />
        <CompanyFields form={form} />
        <LocationFields form={form} />
        <AdditionalFields form={form} />
        <CategoryField form={form} />
        <UserSelector form={form} />
        <div className="flex gap-2 justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button 
              type="submit" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : initialData ? 'Update Contact' : 'Create Contact'}
            </Button>
            {!initialData && (
              <Button 
                type="button" 
                disabled={isSubmitting}
                onClick={() => form.handleSubmit((values) => handleSubmit(values, true))()}
              >
                {isSubmitting ? 'Saving...' : 'Save & Create Another'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Form>
  );
};
